// JSON-LD schema builders. Each returns a plain object; render with
// <script type="application/ld+json">{JSON.stringify(obj)}</script>
// React 19 hoists that script into <head> automatically.

import {
  BRAND,
  CONTACT,
  SHOWROOM,
  SITE_NAME,
  SITE_URL,
  SOCIAL,
  absUrl,
} from "./site";

const openingSpec = (h) =>
  h.opens && h.opens !== "By appointment"
    ? `${h.days.split(" - ").map((d) => d.slice(0, 2)).join("-")} ${h.opens}-${h.closes}`
    : null;

const openingHoursSpecification = SHOWROOM.hours
  .map((h) => {
    if (!h.opens || h.opens === "By appointment") return null;
    const daysMap = {
      Monday: "Monday",
      Tuesday: "Tuesday",
      Wednesday: "Wednesday",
      Thursday: "Thursday",
      Friday: "Friday",
      Saturday: "Saturday",
      Sunday: "Sunday",
    };
    // "Monday - Friday" → [Monday, Tuesday, Wednesday, Thursday, Friday]
    let days = [];
    if (h.days.includes(" - ")) {
      const [a, b] = h.days.split(" - ");
      const order = Object.keys(daysMap);
      const start = order.indexOf(a);
      const end = order.indexOf(b);
      if (start >= 0 && end >= 0)
        days = order.slice(start, end + 1);
    } else if (daysMap[h.days]) {
      days = [h.days];
    }
    return {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: days,
      opens: h.opens,
      closes: h.closes,
    };
  })
  .filter(Boolean);

export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: BRAND.legalName,
  alternateName: SITE_NAME,
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: BRAND.logo,
  },
  foundingDate: BRAND.founded,
  areaServed: {
    "@type": "Country",
    name: "New Zealand",
  },
  sameAs: [SOCIAL.instagram, SOCIAL.facebook, SOCIAL.linkedin],
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: CONTACT.phone,
      email: CONTACT.emailPrimary,
      contactType: "sales",
      areaServed: "NZ",
      availableLanguage: ["en"],
    },
  ],
});

export const localBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE_URL}/#localbusiness`,
  name: BRAND.legalName,
  image: BRAND.logo,
  url: SITE_URL,
  telephone: CONTACT.phone,
  email: CONTACT.emailPrimary,
  priceRange: "$$$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: SHOWROOM.street,
    addressLocality: SHOWROOM.suburb,
    addressRegion: SHOWROOM.region,
    postalCode: SHOWROOM.postalCode,
    addressCountry: SHOWROOM.countryCode,
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: SHOWROOM.latitude,
    longitude: SHOWROOM.longitude,
  },
  openingHoursSpecification,
  areaServed: [
    { "@type": "Country", name: "New Zealand" },
    { "@type": "City", name: "Auckland" },
    { "@type": "City", name: "Wellington" },
    { "@type": "City", name: "Christchurch" },
    { "@type": "City", name: "Queenstown" },
  ],
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: SITE_NAME,
  publisher: { "@id": `${SITE_URL}/#organization` },
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
  inLanguage: "en-NZ",
});

// items = [{ name, path }] — pass absolute path per crumb.
export const breadcrumbSchema = (items = []) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.name,
    item: absUrl(c.path),
  })),
});

// Category-level Product schema. Uses ItemList wrapping when there are many
// SKUs, but for MVP we emit one Product per page representing the family.
export const productSchema = ({
  name,
  description,
  image,
  sku,
  slug,
  category,
}) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name,
  description,
  image,
  sku: sku || slug,
  brand: { "@type": "Brand", name: SITE_NAME },
  category,
  url: absUrl(`/products/${slug}`),
  offers: {
    "@type": "Offer",
    availability: "https://schema.org/InStock",
    priceCurrency: "NZD",
    price: "0",
    priceValidUntil: "2099-12-31",
    url: absUrl(`/products/${slug}`),
    areaServed: { "@type": "Country", name: "New Zealand" },
    seller: { "@id": `${SITE_URL}/#organization` },
  },
});

// items = [{ question, answer }]
export const faqPageSchema = (items = []) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: f.answer,
    },
  })),
});
